/** @typedef {'menu' | 'inline' | 'team_picker' | 'match_picker'} FlowNodeType */

/**
 * Declarative help wizard flow (see docs/Dashboard Help Buttons — Developer Guide.md).
 * Labels resolved via i18n keys under phase2.playerHelpWizard.nodes.{nodeId}.*
 */

export const FLOW_ROOT = 'root';

/** @type {Record<string, object>} */
export const FLOW_NODES = {
  root: {
    type: 'menu',
    titleKey: 'phase2.playerHelpWizard.nodes.root.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.root.body',
    choices: [
      { next: 'injury', labelKey: 'phase2.playerHelpWizard.nodes.root.choiceInjury', emoji: '🤕' },
      { next: 'opponent_silent', labelKey: 'phase2.playerHelpWizard.nodes.root.choiceOpponent', emoji: '⏰' },
      { next: 'reschedule', labelKey: 'phase2.playerHelpWizard.nodes.root.choiceReschedule', emoji: '🔁' },
      { next: 'replace_player', labelKey: 'phase2.playerHelpWizard.nodes.root.choiceReplace', emoji: '👤' },
      { next: 'injury_forfeit', labelKey: 'phase2.playerHelpWizard.nodes.root.choiceForfeit', emoji: '🏳️' },
      { next: 'other', labelKey: 'phase2.playerHelpWizard.nodes.root.choiceOther', emoji: '❓' },
    ],
  },

  injury: {
    type: 'menu',
    titleKey: 'phase2.playerHelpWizard.nodes.injury.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injury.body',
    choices: [
      { next: 'injury_temp', labelKey: 'phase2.playerHelpWizard.nodes.injury.choiceTemp', emoji: '📅' },
      { next: 'holiday_temp', labelKey: 'phase2.playerHelpWizard.nodes.injury.choiceHoliday', emoji: '🏖️' },
      { next: 'injury_replace', labelKey: 'phase2.playerHelpWizard.nodes.injury.choiceReplace', emoji: '🔄' },
      { next: 'injury_find', labelKey: 'phase2.playerHelpWizard.nodes.injury.choiceFind', emoji: '🔍' },
      { next: 'injury_forfeit', labelKey: 'phase2.playerHelpWizard.nodes.injury.choiceForfeit', emoji: '🏳️' },
    ],
  },

  injury_temp: {
    type: 'inline',
    widget: 'injury_period',
    titleKey: 'phase2.playerHelpWizard.nodes.injuryTemp.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injuryTemp.body',
    hintKey: 'phase2.playerHelpWizard.hints.injuryTemp',
  },

  holiday_temp: {
    type: 'inline',
    widget: 'holiday_period',
    titleKey: 'phase2.playerHelpWizard.nodes.holidayTemp.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.holidayTemp.body',
    hintKey: 'phase2.playerHelpWizard.hints.holidayTemp',
  },

  injury_replace: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.injuryReplace.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injuryReplace.body',
    hintKey: 'phase2.playerHelpWizard.hints.injuryReplace',
    next: 'injury_replace_action',
  },

  injury_replace_action: {
    type: 'inline',
    widget: 'roster_manage',
    requiresTeam: true,
    titleKey: 'phase2.playerHelpWizard.nodes.injuryReplace.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injuryReplace.body',
    hintKey: 'phase2.playerHelpWizard.hints.injuryReplaceAction',
  },

  injury_find: {
    type: 'inline',
    widget: 'find_players',
    titleKey: 'phase2.playerHelpWizard.nodes.injuryFind.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injuryFind.body',
    hintKey: 'phase2.playerHelpWizard.hints.injuryFind',
  },

  injury_forfeit: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.injuryForfeit.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injuryForfeit.body',
    hintKey: 'phase2.playerHelpWizard.hints.injuryForfeit',
    next: 'injury_forfeit_confirm',
  },

  injury_forfeit_confirm: {
    type: 'inline',
    widget: 'season_forfeit',
    requiresTeam: true,
    titleKey: 'phase2.playerHelpWizard.nodes.injuryForfeitConfirm.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.injuryForfeitConfirm.body',
    hintKey: 'phase2.playerHelpWizard.hints.injuryForfeitConfirm',
  },

  reschedule: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.reschedule.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.reschedule.body',
    hintKey: 'phase2.playerHelpWizard.hints.reschedule',
    next: 'reschedule_match',
  },

  reschedule_match: {
    type: 'match_picker',
    openChatOnPick: true,
    requireRescheduleAck: true,
    titleKey: 'phase2.playerHelpWizard.nodes.rescheduleMatch.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.rescheduleMatch.body',
    hintKey: 'phase2.playerHelpWizard.hints.rescheduleMatch',
  },

  replace_player: {
    type: 'menu',
    titleKey: 'phase2.playerHelpWizard.nodes.replacePlayer.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.replacePlayer.body',
    choices: [
      { next: 'replace_roster', labelKey: 'phase2.playerHelpWizard.nodes.replacePlayer.choiceRoster', emoji: '👤' },
      { next: 'replace_find', labelKey: 'phase2.playerHelpWizard.nodes.replacePlayer.choiceFind', emoji: '🔍' },
      { next: 'replace_invite', labelKey: 'phase2.playerHelpWizard.nodes.replacePlayer.choiceInvite', emoji: '✉️' },
      { next: 'leave_team', labelKey: 'phase2.playerHelpWizard.nodes.replacePlayer.choiceForfeitSeason', emoji: '🚪' },
    ],
  },

  replace_roster: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.replaceRoster.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.replaceRoster.body',
    hintKey: 'phase2.playerHelpWizard.hints.replaceRoster',
    next: 'replace_roster_action',
  },

  replace_roster_action: {
    type: 'inline',
    widget: 'roster_manage',
    requiresTeam: true,
    titleKey: 'phase2.playerHelpWizard.nodes.replaceRosterAction.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.replaceRosterAction.body',
    hintKey: 'phase2.playerHelpWizard.hints.replaceRosterAction',
  },

  replace_find: {
    type: 'inline',
    widget: 'find_players',
    titleKey: 'phase2.playerHelpWizard.nodes.replaceFind.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.replaceFind.body',
    hintKey: 'phase2.playerHelpWizard.hints.replaceFind',
  },

  replace_invite: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.replaceInvite.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.replaceInvite.body',
    hintKey: 'phase2.playerHelpWizard.hints.replaceInvite',
    next: 'replace_invite_action',
  },

  replace_invite_action: {
    type: 'inline',
    widget: 'invite_player',
    requiresTeam: true,
    titleKey: 'phase2.playerHelpWizard.nodes.replaceInviteAction.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.replaceInviteAction.body',
    hintKey: 'phase2.playerHelpWizard.hints.replaceInviteAction',
  },

  leave_team: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.leaveTeam.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.leaveTeam.body',
    hintKey: 'phase2.playerHelpWizard.hints.leaveTeam',
    next: 'leave_team_confirm',
  },

  leave_team_confirm: {
    type: 'inline',
    widget: 'self_leave_team',
    requiresTeam: true,
    titleKey: 'phase2.playerHelpWizard.nodes.leaveTeamConfirm.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.leaveTeamConfirm.body',
    hintKey: 'phase2.playerHelpWizard.hints.leaveTeamConfirm',
  },

  opponent_silent: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.opponentSilent.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.opponentSilent.body',
    hintKey: 'phase2.playerHelpWizard.hints.opponentSilent',
    next: 'opponent_silent_match',
  },

  opponent_silent_match: {
    type: 'match_picker',
    widget: 'opponent_contact',
    titleKey: 'phase2.playerHelpWizard.nodes.opponentSilentMatch.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.opponentSilentMatch.body',
    hintKey: 'phase2.playerHelpWizard.hints.opponentSilentMatch',
  },

  location: {
    type: 'menu',
    titleKey: 'phase2.playerHelpWizard.nodes.location.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.location.body',
    choices: [
      { next: 'location_clubs', labelKey: 'phase2.playerHelpWizard.nodes.location.choiceClubs', emoji: '📍' },
      { next: 'location_court', labelKey: 'phase2.playerHelpWizard.nodes.location.choiceCourt', emoji: '🎾' },
    ],
  },

  location_clubs: {
    type: 'inline',
    widget: 'partner_clubs',
    titleKey: 'phase2.playerHelpWizard.nodes.locationClubs.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.locationClubs.body',
    hintKey: 'phase2.playerHelpWizard.hints.locationClubs',
  },

  location_court: {
    type: 'team_picker',
    titleKey: 'phase2.playerHelpWizard.nodes.locationCourt.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.locationCourt.body',
    hintKey: 'phase2.playerHelpWizard.hints.locationCourt',
    next: 'location_court_action',
  },

  location_court_action: {
    type: 'inline',
    widget: 'court_booking',
    requiresTeam: true,
    titleKey: 'phase2.playerHelpWizard.nodes.locationCourtAction.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.locationCourtAction.body',
    hintKey: 'phase2.playerHelpWizard.hints.locationCourtAction',
  },

  other: {
    type: 'inline',
    widget: 'support_form',
    titleKey: 'phase2.playerHelpWizard.nodes.other.title',
    bodyKey: 'phase2.playerHelpWizard.nodes.other.body',
    hintKey: 'phase2.playerHelpWizard.hints.other',
    supportPreset: {
      category: 'other',
      subjectKey: 'phase2.playerHelpWizard.supportSubjects.other',
    },
  },
};

export function getFlowNode(nodeId) {
  return FLOW_NODES[nodeId] || null;
}
